import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CommentIcon from "@mui/icons-material/Comment";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { DateRangeProvider, useDateRange } from "contexts/DateRangeContext";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  HistoricalHeatOverride,
  HeatOverrideCancellationFilter,
  useListHeatOverrideHistoryQuery,
  generatedApi,
} from "src/store/api/generatedApi";
import {
  CANCELLATION_PARAM,
  DATE_FORMAT,
  DATE_RANGE_PREFIX,
  normaliseOverrideHistorySearchParams,
  resolveOverrideHistoryFilters,
} from "./historyFilters";
import { HistoricalOverrideDialog } from "./HistoricalOverrideDialog";
import { formatOverrideReason } from "./OverrideDetailSections";
import { formatHeatIdRanges } from "src/utils/formatHeatIdRanges";
import { downloadCsv } from "src/utils/csv";
import { RetryErrorAlert } from "components/common/alerts/RetryErrorAlert";

const EXPORT_PAGE_SIZE = 100;
const CANCELLED_ROW_CLASS = "override-history-cancelled";

const overrideHeatIds = (override: HistoricalHeatOverride) =>
  override.scopes.map((scope) => scope.stable_heat_id);

const formatOverrideHeatIds = (override: HistoricalHeatOverride) =>
  overrideHeatIds(override).join(", ");

export const OverrideHistoryPage = () => {
  const { localTimezone } = useLocalDatetime();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = dayjs().tz(localTimezone).format(DATE_FORMAT);
  const { startDate, endDate, cancellation, normalised } =
    resolveOverrideHistoryFilters({ searchParams, today });
  useEffect(() => {
    if (!normalised) {
      setSearchParams(
        (current) =>
          normaliseOverrideHistorySearchParams({
            searchParams: current,
            startDate,
            endDate,
          }),
        { replace: true }
      );
    }
  }, [normalised, startDate, endDate, setSearchParams]);
  if (!normalised) return null;
  return (
    <DateRangeProvider
      config={{
        paramPrefix: DATE_RANGE_PREFIX,
        defaultStartDate: dayjs(startDate),
        defaultEndDate: dayjs(endDate),
      }}
    >
      <OverrideHistoryTable
        key={`${startDate}/${endDate}/${cancellation}`}
        cancellation={cancellation}
      />
    </DateRangeProvider>
  );
};

const OverrideHistoryTable = ({
  cancellation,
}: {
  cancellation: HeatOverrideCancellationFilter;
}) => {
  const [, setSearchParams] = useSearchParams();
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { localTimezone, toLocalDatetime } = useLocalDatetime();
  const { startDate, endDate } = useDateRange();
  const range = {
    tenantName,
    cancellation,
    startDate: startDate.format(DATE_FORMAT),
    endDate: endDate.format(DATE_FORMAT),
  };
  const [fetchHistory] =
    generatedApi.endpoints.listHeatOverrideHistory.useLazyQuery();
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
  const apiRef = useGridApiRef();
  const [selectedOverride, setSelectedOverride] =
    useState<HistoricalHeatOverride | null>(null);
  const history = useListHeatOverrideHistoryQuery(
    {
      ...range,
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
    },
    { refetchOnMountOrArgChange: true }
  );
  useEffect(() => {
    if (history.isFetching || !history.currentData?.items.length) return;
    const frame = requestAnimationFrame(() => {
      void apiRef.current?.autosizeColumns({
        columns: ["first_heat_start_time", "created_at", "scopes"],
        includeHeaders: true,
        includeOutliers: true,
      });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  });
  const formatHeatStart = (override: HistoricalHeatOverride) => {
    switch (override.heat_status) {
      case "unrecognised":
        return t("unrecognizedHeat");
      case "not_started":
        return t("not_started");
      case "started":
        return override.first_heat_start_time === null
          ? "—"
          : toLocalDatetime(override.first_heat_start_time);
    }
  };
  const downloadHistory = async () => {
    setExporting(true);
    setExportFailed(false);
    try {
      const overrides: HistoricalHeatOverride[] = [];
      let page = 0;
      let total: number;
      do {
        page += 1;
        const response = await fetchHistory({
          ...range,
          page,
          pageSize: EXPORT_PAGE_SIZE,
        }).unwrap();
        overrides.push(...response.items);
        total = response.total;
      } while (page * EXPORT_PAGE_SIZE < total);
      downloadCsv({
        rows: overrides,
        columns: [
          {
            header: t("overrideHeatStart"),
            value: formatHeatStart,
          },
          {
            header: t("requested"),
            value: (row) => toLocalDatetime(row.created_at),
          },
          { header: t("overrideReason"), value: (row) => row.reason },
          { header: t("overrideSubreason"), value: (row) => row.subreason },
          { header: t("recipe"), value: (row) => row.recipe_name },
          { header: t("numberOfHeats"), value: (row) => row.scopes.length },
          {
            header: t("heatIds"),
            value: formatOverrideHeatIds,
          },
          { header: t("addedBy"), value: (row) => row.created_by },
          {
            header: t("cancelledAt"),
            value: (row) =>
              row.cancelled_at === null
                ? null
                : toLocalDatetime(row.cancelled_at),
          },
          { header: t("cancelledBy"), value: (row) => row.cancelled_by },
        ],
        filename: `overrides-${range.startDate}-${range.endDate}.csv`,
      });
    } catch {
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  };
  const cancellationDetail = (override: HistoricalHeatOverride) =>
    override.cancelled_at === null
      ? null
      : t("overrideCancelledDetail", {
          time: toLocalDatetime(override.cancelled_at),
          name: override.cancelled_by ?? "—",
        });
  const columns: GridColDef<HistoricalHeatOverride>[] = [
    {
      field: "first_heat_start_time",
      headerName: t("overrideHeatStart"),
      renderCell: ({ row }) => formatHeatStart(row),
    },
    {
      field: "created_at",
      headerName: t("requested"),
      renderCell: ({ row }) => {
        const detail = cancellationDetail(row);
        return (
          <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
            <span>{toLocalDatetime(row.created_at)}</span>
            {detail && (
              <Tooltip title={detail}>
                <Chip
                  label={
                    <Typography variant="caption">
                      {t("overrideCancelled")}
                    </Typography>
                  }
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, "& .MuiChip-label": { px: 0.75 } }}
                />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: "reason",
      headerName: t("overrideReason"),
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          sx={{ gap: 1, alignItems: "center", width: "100%" }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formatOverrideReason(row) || "—"}
          </Box>
          {row.comment && (
            <Tooltip title={row.comment}>
              <CommentIcon
                aria-label={t("comment")}
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: "recipe_name",
      headerName: t("recipe"),
      width: 140,
    },
    {
      field: "scopes",
      headerName: t("heats"),
      width: 140,
      renderCell: ({ row }) => (
        <Tooltip title={formatOverrideHeatIds(row)}>
          <span>{formatHeatIdRanges(overrideHeatIds(row))}</span>
        </Tooltip>
      ),
    },
    { field: "created_by", headerName: t("addedBy"), width: 150 },
  ];
  const renderHistory = () => {
    if (history.isError) {
      return (
        <RetryErrorAlert
          message={t("overrideHistoryLoadError")}
          onRetry={() => {
            void history.refetch();
          }}
        />
      );
    }
    if (history.currentData?.total === 0) {
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("overrideHistoryEmpty")}
        </Typography>
      );
    }
    return (
      <HotkeyCompliantDataGrid
        apiRef={apiRef}
        rows={history.currentData?.items ?? []}
        columns={columns}
        getRowId={(override) => override.override_id}
        onRowClick={({ row }: GridRowParams<HistoricalHeatOverride>) => {
          setSelectedOverride(row);
        }}
        loading={history.isFetching}
        autoHeight
        pagination
        hideFooter={false}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[25, 50, 100]}
        rowCount={history.data?.total ?? 0}
        disableColumnMenu
        disableColumnSorting
        disableRowSelectionOnClick
        getRowClassName={({ row }) =>
          row.cancelled_at === null ? "" : CANCELLED_ROW_CLASS
        }
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-cell": { lineHeight: "calc(var(--height) - 1px)" },
          [`& .${CANCELLED_ROW_CLASS}`]: { color: "text.secondary" },
        }}
      />
    );
  };
  return (
    <AccountPageLayout
      title={t("overrideHistory")}
      actionButton={
        <DateRangeSelector
          rangeType="inclusiveDates"
          timezone={localTimezone}
          actions={
            <>
              <Button
                startIcon={<CloudDownloadIcon />}
                disabled={
                  exporting ||
                  history.isFetching ||
                  history.isError ||
                  !history.currentData?.total
                }
                onClick={() => {
                  void downloadHistory();
                }}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("downloadCsv")}
              </Button>
              <FormControlLabel
                label={t("showCancelled")}
                sx={{ whiteSpace: "nowrap" }}
                control={
                  <Checkbox
                    checked={cancellation === "include"}
                    onChange={(_, checked) => {
                      setSearchParams(
                        (current) => {
                          const next = new URLSearchParams(current);
                          next.set(
                            CANCELLATION_PARAM,
                            checked ? "include" : "exclude"
                          );
                          return next;
                        },
                        { replace: true }
                      );
                    }}
                  />
                }
              />
            </>
          }
        />
      }
    >
      {exportFailed && (
        <RetryErrorAlert
          message={t("overrideHistoryLoadError")}
          onRetry={() => {
            void downloadHistory();
          }}
        />
      )}
      <Box sx={{ width: "100%", mt: 1 }}>{renderHistory()}</Box>
      {selectedOverride && (
        <HistoricalOverrideDialog
          override={selectedOverride}
          onSaved={setSelectedOverride}
          onClose={() => {
            setSelectedOverride(null);
          }}
        />
      )}
    </AccountPageLayout>
  );
};
